"use client";

import { useState } from "react";
import {
    ArrowDown,
    ArrowDownRight,
    ArrowRight,
    ArrowUpRight,
    GripVertical,
    Image as ImageIcon,
    List,
    Plus,
    SlidersHorizontal,
    Trash2,
} from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ImageFill,
    TableAppearance,
    TextBackground,
    TextBlock,
    TextColumn,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import {
    resolveTableAppearance,
    TABLE_CONFIG,
    TEXT_VARIANT_LABEL,
    tableModel,
} from "../../lib/blockRegistry";
import { DEFAULT_THEME, mixHex } from "../../lib/theme";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    AppearanceStepper,
    ColorChipField,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";
import { Slider } from "@/components/ui/slider";
import LomdaUploadFile from "../blocks/shared/lomdaUploadFile";
import { IMAGE_UPLOAD } from "../../lib/uploadLimits";

/** Mirrors the image block's modes so both speak the same language. */
const BAND_FILL_MODES: { value: ImageFill; labelKey: string }[] = [
    { value: "crop", labelKey: "lomda-v3-editor.media.crop" },
    { value: "fit", labelKey: "lomda-v3-editor.media.fit" },
    { value: "fill", labelKey: "lomda-v3-editor.media.fill" },
];

/** The nine positions of a focal-point grid, as background-position percents. */
const FOCAL_POINTS: [number, number][] = [
    [0, 0], [50, 0], [100, 0],
    [0, 50], [50, 50], [100, 50],
    [0, 100], [50, 100], [100, 100],
];

/** What "Fixed" starts at — a band you can see, without dwarfing the text. */
const DEFAULT_BAND_HEIGHT = 320;

const HEIGHT_MODES: { mode: "auto" | "full" | "fixed"; labelKey: string }[] = [
    { mode: "auto", labelKey: "lomda-v3-editor.text.background-height-auto" },
    { mode: "full", labelKey: "lomda-v3-editor.text.background-height-full" },
    { mode: "fixed", labelKey: "lomda-v3-editor.text.background-height-fixed" },
];

/** CSS gradient angles: 180deg points down, 90deg to the right. Physical on
 *  purpose — a gradient is a picture, and pictures don't mirror in Hebrew. */
const GRADIENT_ANGLES: { angle: number; icon: typeof ArrowDown }[] = [
    { angle: 180, icon: ArrowDown },
    { angle: 135, icon: ArrowDownRight },
    { angle: 90, icon: ArrowRight },
    { angle: 45, icon: ArrowUpRight },
];

const MIN_COLUMNS = 1;
const MAX_COLUMNS = 4;
/** Column headings are rich (HTML); show plain text in the manage list. */
const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "");

/**
 * Text block topbar: the block's name + (for multi-columns) a column stepper.
 * The layout/variant switcher was removed — variant is not changed from the
 * top navbar.
 */
export default function TextSettings({ block }: { block: TextBlock }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const patchBlockBackground = useLomdaV3Editor(
        (s) => s.patchBlockBackground
    );
    const theme = useLomdaV3Editor((s) => s.metadata.theme);

    const data = block.data;
    const isColumns =
        data.variant === "multi-columns" ||
        data.variant === "two-columns" ||
        data.variant === "three-columns";

    const table = data.variant === "table" ? tableModel(data) : null;
    const colCount = table?.rows[0]?.length ?? 0;
    const rowCount = table?.rows.length ?? 0;
    const setTable = (rows: string[][], headerRow: boolean) =>
        updateBlock(block.id, { rows, headerRow, html: undefined });
    const addColumn = () => {
        if (table) setTable(table.rows.map((r) => [...r, ""]), table.headerRow);
    };
    const removeColumn = () => {
        if (table && colCount > 1)
            setTable(
                table.rows.map((r) => r.slice(0, -1)),
                table.headerRow
            );
    };
    const addRow = () => {
        if (table)
            setTable(
                [...table.rows, Array(colCount).fill("")],
                table.headerRow
            );
    };
    const removeRow = () => {
        if (table && rowCount > 1)
            setTable(table.rows.slice(0, -1), table.headerRow);
    };
    const toggleHeader = () => {
        if (table) setTable(table.rows, !table.headerRow);
    };
    const tableAppearance =
        data.variant === "table"
            ? resolveTableAppearance(data.appearance, theme)
            : null;
    const setTableAppearance = (patch: Partial<TableAppearance>) => {
        if (tableAppearance)
            updateBlock(block.id, {
                appearance: { ...tableAppearance, ...patch },
            });
    };

    const background = block.background;
    const setBackground = (patch: Partial<TextBackground>) =>
        patchBlockBackground(block.id, patch);

    return (
        <div className="flex items-center gap-3">
            <span className="px-2 py-1 text-sm !font-semibold text-foreground">
                {t(TEXT_VARIANT_LABEL[data.variant])}
            </span>
            <WidthControl blockId={block.id} />
            <BackgroundPopover
                background={background}
                onChange={setBackground}
                onReset={() => patchBlockBackground(block.id, undefined)}
            />

            {isColumns && (
                <>
                    <span className="mx-0.5 h-5 w-px bg-border" />
                    <ManageColumnsPopover
                        columns={data.columns}
                        onChange={(columns) =>
                            updateBlock(block.id, { columns })
                        }
                    />
                </>
            )}

            {table && tableAppearance && (
                <>
                    <span className="mx-0.5 h-5 w-px bg-border" />
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground">
                            {t("lomda-v3-editor.text.columns")}
                        </span>
                        <AppearanceStepper
                            value={colCount}
                            onDec={removeColumn}
                            onInc={addColumn}
                            canDec={colCount > 1}
                            canInc={colCount < TABLE_CONFIG.maxColumns}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground">
                            {t("lomda-v3-editor.text.rows")}
                        </span>
                        <AppearanceStepper
                            value={rowCount}
                            onDec={removeRow}
                            onInc={addRow}
                            canDec={rowCount > 1}
                            canInc={rowCount < TABLE_CONFIG.maxRows}
                        />
                    </div>
                    <TableAppearancePopover
                        appearance={tableAppearance}
                        onChange={setTableAppearance}
                        headerRow={table.headerRow}
                        onToggleHeader={toggleHeader}
                        onReset={() =>
                            updateBlock(block.id, { appearance: undefined })
                        }
                    />
                </>
            )}
        </div>
    );
}

function ManageColumnsPopover({
    columns,
    onChange,
}: {
    columns: TextColumn[];
    onChange: (columns: TextColumn[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...columns];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <List className="size-5" />
                    {t("lomda-v3-editor.text.manage-columns")}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[412px] rounded-2xl p-4">
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.text.manage-columns")}
                    </PopoverTitle>
                    <div className="flex flex-col gap-1">
                        {columns.map((col, i) => (
                            <div
                                key={i}
                                draggable
                                onDragStart={() => setDragIndex(i)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                    if (dragIndex !== null) move(dragIndex, i);
                                    setDragIndex(null);
                                }}
                                className="flex items-center gap-2.5 rounded-lg px-1 py-1.5"
                            >
                                <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                                <span className="text-sm text-primary">
                                    {i + 1}
                                </span>
                                <input
                                    value={stripHtml(col.heading)}
                                    onChange={(e) =>
                                        onChange(
                                            columns.map((x, xi) =>
                                                xi === i
                                                    ? {
                                                          ...x,
                                                          heading: e.target.value,
                                                      }
                                                    : x
                                            )
                                        )
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.text-variant.heading-placeholder"
                                    )}
                                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                />
                                <button
                                    type="button"
                                    disabled={columns.length <= MIN_COLUMNS}
                                    onClick={() =>
                                        onChange(
                                            columns.filter((_, xi) => xi !== i)
                                        )
                                    }
                                    className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                                >
                                    <Trash2 className="size-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        disabled={columns.length >= MAX_COLUMNS}
                        onClick={() =>
                            onChange([...columns, { heading: "", text: "" }])
                        }
                        className="flex items-center gap-2 self-start rounded-lg text-sm text-muted-foreground transition hover:text-primary disabled:opacity-30"
                    >
                        <Plus className="size-4" />
                        {t("lomda-v3-editor.text.add-column")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function TableAppearancePopover({
    appearance,
    onChange,
    headerRow,
    onToggleHeader,
    onReset,
}: {
    appearance: TableAppearance;
    onChange: (patch: Partial<TableAppearance>) => void;
    headerRow: boolean;
    onToggleHeader: () => void;
    onReset: () => void;
}) {
    const [colors] = TABLE_CONFIG.appearanceGroups;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.table.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.table.appearance")}
                    </PopoverTitle>

                    {/* Division 1 — colors + header-row toggle */}
                    {colors.map((field) => {
                        if (field.control === "switch") {
                            return (
                                <AppearanceRow
                                    key={field.key}
                                    label={t(field.labelKey)}
                                >
                                    <Switch
                                        checked={headerRow}
                                        onCheckedChange={onToggleHeader}
                                    />
                                </AppearanceRow>
                            );
                        }
                        const apKey = field.key as keyof TableAppearance;
                        const value = appearance[apKey];
                        return (
                            <AppearanceRow
                                key={field.key}
                                label={t(field.labelKey)}
                            >
                                {field.control === "borderWidth" ? (
                                    <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                        <input
                                            type="number"
                                            min={0}
                                            max={8}
                                            value={Number(value)}
                                            onChange={(e) =>
                                                onChange({
                                                    [apKey]: Math.max(
                                                        0,
                                                        parseInt(
                                                            e.target.value,
                                                            10
                                                        ) || 0
                                                    ),
                                                })
                                            }
                                            className="w-6 bg-transparent text-center outline-none"
                                        />
                                        <span className="text-muted-foreground">
                                            px
                                        </span>
                                    </div>
                                ) : (
                                    <ColorChipField
                                        value={String(value)}
                                        onChange={(c) =>
                                            onChange({ [apKey]: c })
                                        }
                                    />
                                )}
                            </AppearanceRow>
                        );
                    })}
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}

/**
 * The section background: a colour and/or an image painted behind the text.
 * By default it leaves the content column and fills the page — the text stays
 * where it is, only the background widens.
 */
function BackgroundPopover({
    background,
    onChange,
    onReset,
}: {
    background: TextBackground | undefined;
    onChange: (patch: Partial<TextBackground>) => void;
    onReset: () => void;
}) {
    const userId = useLomdaV3Editor((s) => s.userId);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const [uploadNonce] = useState(() => Date.now().toString(36));
    const overlayPct = Math.round((background?.overlay ?? 0) * 100);
    const fixedHeight = background?.minHeight;
    const baseColor =
        background?.color ?? theme?.background ?? DEFAULT_THEME.background;
    const heightMode = background?.fullHeight
        ? "full"
        : fixedHeight !== undefined
          ? "fixed"
          : "auto";

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <ImageIcon className="size-5" />
                    {t("lomda-v3-editor.text.background")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.text.background")}
                    </PopoverTitle>

                    <AppearanceRow
                        label={t("lomda-v3-editor.text.background-color")}
                    >
                        <ColorChipField
                            value={baseColor}
                            onChange={(color) => onChange({ color })}
                        />
                    </AppearanceRow>

                    <AppearanceRow
                        label={t("lomda-v3-editor.text.background-gradient")}
                    >
                        <Switch
                            checked={!!background?.colorTo}
                            onCheckedChange={(on) =>
                                onChange({
                                    color: baseColor,
                                    // A lighter tint of the first colour, so
                                    // turning it on reads as a gradient at once
                                    // instead of a flat block.
                                    colorTo: on
                                        ? mixHex(baseColor, "#FFFFFF", 0.45)
                                        : undefined,
                                })
                            }
                        />
                    </AppearanceRow>

                    {background?.colorTo && (
                        <>
                            <AppearanceRow
                                label={t(
                                    "lomda-v3-editor.text.background-color-to"
                                )}
                            >
                                <ColorChipField
                                    value={background.colorTo}
                                    onChange={(colorTo) => onChange({ colorTo })}
                                />
                            </AppearanceRow>
                            <AppearanceRow
                                label={t(
                                    "lomda-v3-editor.text.background-gradient-direction"
                                )}
                            >
                                <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                                    {GRADIENT_ANGLES.map((g) => {
                                        const Icon = g.icon;
                                        const on =
                                            (background.gradientAngle ?? 180) ===
                                            g.angle;
                                        return (
                                            <button
                                                key={g.angle}
                                                type="button"
                                                aria-label={`${g.angle}°`}
                                                onClick={() =>
                                                    onChange({
                                                        gradientAngle: g.angle,
                                                    })
                                                }
                                                className={cn(
                                                    "flex size-7 items-center justify-center rounded-md transition",
                                                    on
                                                        ? "bg-accent text-foreground"
                                                        : "text-muted-foreground hover:text-foreground"
                                                )}
                                            >
                                                <Icon className="size-4" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </AppearanceRow>
                        </>
                    )}

                    <AppearanceDivider />

                    <span className="text-sm text-foreground">
                        {t("lomda-v3-editor.text.background-image")}
                    </span>
                    {background?.imageUrl ? (
                        <>
                            <div className="flex items-center gap-2">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={background.imageUrl}
                                    alt=""
                                    className="h-14 w-20 rounded-lg border object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        onChange({ imageUrl: undefined })
                                    }
                                    className="flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-destructive"
                                >
                                    <Trash2 className="size-4" />
                                    {t("lomda-v3-editor.text.background-remove")}
                                </button>
                            </div>
                            {/* Same three modes as the image block, so an
                                author meets one vocabulary for images. */}
                            <AppearanceRow
                                label={t("lomda-v3-editor.media.fill")}
                            >
                                <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                                    {BAND_FILL_MODES.map((m) => (
                                        <button
                                            key={m.value}
                                            type="button"
                                            onClick={() =>
                                                onChange({ fill: m.value })
                                            }
                                            className={cn(
                                                "rounded-md px-2 py-1 text-xs transition",
                                                (background?.fill ?? "crop") ===
                                                    m.value
                                                    ? "bg-accent text-foreground"
                                                    : "text-muted-foreground hover:text-foreground"
                                            )}
                                        >
                                            {t(m.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </AppearanceRow>

                            {/* Which part of the photo survives the crop. A
                                wide, short band cuts most of it away. */}
                            <AppearanceRow
                                label={t("lomda-v3-editor.text.background-focal")}
                            >
                                <div className="grid grid-cols-3 gap-0.5 rounded-lg border border-border p-0.5">
                                    {FOCAL_POINTS.map(([x, y]) => {
                                        const on =
                                            (background?.focalX ?? 50) === x &&
                                            (background?.focalY ?? 50) === y;
                                        return (
                                            <button
                                                key={`${x}-${y}`}
                                                type="button"
                                                aria-label={`${x}% ${y}%`}
                                                onClick={() =>
                                                    onChange({
                                                        focalX: x,
                                                        focalY: y,
                                                    })
                                                }
                                                className={cn(
                                                    "size-5 rounded-sm transition",
                                                    on
                                                        ? "bg-[hsl(var(--platform-primary))]"
                                                        : "bg-muted hover:bg-accent"
                                                )}
                                            />
                                        );
                                    })}
                                </div>
                            </AppearanceRow>

                            {/* Same overlay control the lomda background and the
                                flip cards use — a busy photo needs darkening
                                before text can sit on it. */}
                            <AppearanceRow
                                label={t("lomda-v3-editor.text.background-overlay")}
                            >
                                <div className="flex items-center gap-2">
                                    <Slider
                                        min={0}
                                        max={80}
                                        step={5}
                                        value={[overlayPct]}
                                        onValueChange={([v]) =>
                                            onChange({
                                                overlay:
                                                    v > 0 ? v / 100 : undefined,
                                            })
                                        }
                                        className="w-32"
                                    />
                                    <span
                                        dir="ltr"
                                        className="w-9 text-end text-sm text-muted-foreground"
                                    >
                                        {overlayPct}%
                                    </span>
                                </div>
                            </AppearanceRow>
                        </>
                    ) : (
                        userId && (
                            <LomdaUploadFile
                                reuse="image"
                                folder={`section-backgrounds/${uploadNonce}`}
                                options={{
                                    ...IMAGE_UPLOAD,
                                    showImagePreview: false,
                                    showSelectedFileName: false,
                                    purgeCache: true,
                                    className: "h-20 w-full",
                                    title: {
                                        text: t("lomda-v3-editor.text.background-upload"),
                                        keyName: "lomda-v3-editor.text.background-upload",
                                    },
                                    subtitle: {
                                        text: "PNG, JPG, WEBP",
                                        keyName: "brand-kits.backgrounds.upload-subtitle",
                                    },
                                }}
                                onUploadFinished={(f) =>
                                    onChange({ imageUrl: f.fileUrl })
                                }
                            />
                        )
                    )}

                    <AppearanceDivider />

                    {/* Auto = as tall as the text. Fixed = a floor the text can
                        still grow past, set here or by dragging the band's
                        bottom edge on the canvas. */}
                    <AppearanceRow
                        label={t("lomda-v3-editor.text.background-height")}
                    >
                        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                            {HEIGHT_MODES.map((m) => (
                                <button
                                    key={m.mode}
                                    type="button"
                                    onClick={() =>
                                        onChange({
                                            fullHeight: m.mode === "full",
                                            minHeight:
                                                m.mode === "fixed"
                                                    ? (fixedHeight ??
                                                      DEFAULT_BAND_HEIGHT)
                                                    : undefined,
                                        })
                                    }
                                    className={cn(
                                        "rounded-md px-2.5 py-1 text-xs transition",
                                        heightMode === m.mode
                                            ? "bg-accent text-foreground"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    {t(m.labelKey)}
                                </button>
                            ))}
                        </div>
                    </AppearanceRow>

                    {heightMode === "fixed" && (
                        <AppearanceRow label="">
                            <div className="flex h-9 w-[110px] items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                <input
                                    type="number"
                                    dir="ltr"
                                    min={0}
                                    value={fixedHeight}
                                    onChange={(e) => {
                                        const n = parseInt(e.target.value, 10);
                                        onChange({
                                            minHeight: Number.isNaN(n)
                                                ? 0
                                                : Math.max(0, n),
                                        });
                                    }}
                                    className="w-full bg-transparent text-center outline-none"
                                />
                                <span className="text-muted-foreground">px</span>
                            </div>
                        </AppearanceRow>
                    )}

                    <AppearanceRow
                        label={t("lomda-v3-editor.text.background-full-width")}
                    >
                        <Switch
                            checked={background?.fullWidth !== false}
                            onCheckedChange={(v) => onChange({ fullWidth: v })}
                        />
                    </AppearanceRow>

                    {/* Always present, so the popover doesn't reflow as the
                        text is placed and unplaced — just inert until there is
                        a position to undo. */}
                    <button
                        type="button"
                        disabled={background?.textX === undefined}
                        onClick={() =>
                            onChange({ textX: undefined, textY: undefined })
                        }
                        className="self-start text-sm text-muted-foreground transition hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                    >
                        {t("lomda-v3-editor.text.background-reset-position")}
                    </button>

                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}
